import React, { useState } from "react";
import "../../../assets/css/admin/SystemSettings.css";
import AdminLayout from "../../../layouts/AdminLayout/AdminLayout";

const SystemSettings = () => {
  const [activeSection, setActiveSection] = useState("general");
  const [showSaveNotification, setShowSaveNotification] = useState(false);

  // General Settings State
  const [generalSettings, setGeneralSettings] = useState({
    platformName: "Tech Mentor Hub",
    platformEmail: "admin@techmentorhub.com",
    timezone: "Asia/Karachi",
    language: "en",
    dateFormat: "DD/MM/YYYY",
    maintenanceMode: false,
  });

  // Security Settings State
  const [securitySettings, setSecuritySettings] = useState({
    passwordExpiry: 90,
    sessionTimeout: 30,
    loginAttempts: 5,
  });

  // Email Settings State
  const [emailSettings, setEmailSettings] = useState({
    smtpHost: "smtp.gmail.com",
    smtpPort: "587",
    smtpUser: "notifications@techmentorhub.com",
    smtpEncryption: "TLS",
    emailNotifications: true,
    welcomeEmails: true,
    courseUpdates: true,
  });

  // Payment Settings State
  const [paymentSettings, setPaymentSettings] = useState({
    currency: "USD",
    stripeEnabled: true,
    paypalEnabled: true,
    commissionRate: 15,
    minimumPayout: 100,
    autoPayouts: false,
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    pushNotifications: true,
    emailDigest: "daily",
    studentRegistration: true,
    systemAlerts: true,
  });

  // API Settings State
  const [apiSettings, setApiSettings] = useState({
    apiKey: "sk_live_51H*********************",
    rateLimitPerHour: 1000,
    webhooksEnabled: true,
    webhookUrl: "https://api.techmentorhub.com/webhooks",
    apiVersion: "v2.0",
  });

  const handleSave = () => {
    setShowSaveNotification(true);
    setTimeout(() => setShowSaveNotification(false), 3000);
  };

  const renderGeneralSettings = () => (
    <div className="settings-section">
      <h3>General Settings</h3>
      <p className="section-description">
        Configure basic platform settings and preferences
      </p>

      <div className="settings-grid">
        <div className="setting-item">
          <label>Platform Name</label>
          <input
            type="text"
            value={generalSettings.platformName}
            onChange={(e) =>
              setGeneralSettings({
                ...generalSettings,
                platformName: e.target.value,
              })
            }
          />
        </div>

        <div className="setting-item">
          <label>Platform Email</label>
          <input
            type="email"
            value={generalSettings.platformEmail}
            onChange={(e) =>
              setGeneralSettings({
                ...generalSettings,
                platformEmail: e.target.value,
              })
            }
          />
        </div>

        <div className="setting-item">
          <label>Timezone</label>
          <select
            value={generalSettings.timezone}
            onChange={(e) =>
              setGeneralSettings({
                ...generalSettings,
                timezone: e.target.value,
              })
            }
          >
            <option value="Asia/Karachi">Asia/Karachi (PKT)</option>
            <option value="America/New_York">America/New York (EST)</option>
            <option value="Europe/London">Europe/London (GMT)</option>
            <option value="Asia/Dubai">Asia/Dubai (GST)</option>
          </select>
        </div>

        <div className="setting-item">
          <label>Language</label>
          <select
            value={generalSettings.language}
            onChange={(e) =>
              setGeneralSettings({
                ...generalSettings,
                language: e.target.value,
              })
            }
          >
            <option value="en">English</option>
            <option value="ur">Urdu</option>
            <option value="ar">Arabic</option>
          </select>
        </div>

        <div className="setting-item">
          <label>Date Format</label>
          <select
            value={generalSettings.dateFormat}
            onChange={(e) =>
              setGeneralSettings({
                ...generalSettings,
                dateFormat: e.target.value,
              })
            }
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>Maintenance Mode</label>
              <p className="setting-description">
                Enable this to show maintenance page to users
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={generalSettings.maintenanceMode}
                onChange={(e) =>
                  setGeneralSettings({
                    ...generalSettings,
                    maintenanceMode: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h3>Security Settings</h3>
      <p className="section-description">
        Manage security and authentication settings
      </p>

      <div className="settings-grid">
        <div className="setting-item">
          <label>Password Expiry (days)</label>
          <input
            type="number"
            value={securitySettings.passwordExpiry}
            onChange={(e) =>
              setSecuritySettings({
                ...securitySettings,
                passwordExpiry: e.target.value,
              })
            }
          />
          <p className="setting-description">
            Force users to change password after this period
          </p>
        </div>

        <div className="setting-item">
          <label>Session Timeout (minutes)</label>
          <input
            type="number"
            value={securitySettings.sessionTimeout}
            onChange={(e) =>
              setSecuritySettings({
                ...securitySettings,
                sessionTimeout: e.target.value,
              })
            }
          />
        </div>

        <div className="setting-item">
          <label>Max Login Attempts</label>
          <input
            type="number"
            value={securitySettings.loginAttempts}
            onChange={(e) =>
              setSecuritySettings({
                ...securitySettings,
                loginAttempts: e.target.value,
              })
            }
          />
        </div>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="settings-section">
      <h3>Email Configuration</h3>
      <p className="section-description">
        Configure SMTP and email notification settings
      </p>

      <div className="settings-grid">
        <div className="setting-item">
          <label>SMTP Host</label>
          <input
            type="text"
            value={emailSettings.smtpHost}
            onChange={(e) =>
              setEmailSettings({ ...emailSettings, smtpHost: e.target.value })
            }
          />
        </div>

        <div className="setting-item">
          <label>SMTP Port</label>
          <input
            type="text"
            value={emailSettings.smtpPort}
            onChange={(e) =>
              setEmailSettings({ ...emailSettings, smtpPort: e.target.value })
            }
          />
        </div>

        <div className="setting-item">
          <label>SMTP Username</label>
          <input
            type="email"
            value={emailSettings.smtpUser}
            onChange={(e) =>
              setEmailSettings({ ...emailSettings, smtpUser: e.target.value })
            }
          />
        </div>

        <div className="setting-item">
          <label>SMTP Encryption</label>
          <select
            value={emailSettings.smtpEncryption}
            onChange={(e) =>
              setEmailSettings({
                ...emailSettings,
                smtpEncryption: e.target.value,
              })
            }
          >
            <option value="TLS">TLS</option>
            <option value="SSL">SSL</option>
            <option value="None">None</option>
          </select>
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>Email Notifications</label>
              <p className="setting-description">
                Enable email notifications system-wide
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={emailSettings.emailNotifications}
                onChange={(e) =>
                  setEmailSettings({
                    ...emailSettings,
                    emailNotifications: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>Welcome Emails</label>
              <p className="setting-description">
                Send welcome email to new users
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={emailSettings.welcomeEmails}
                onChange={(e) =>
                  setEmailSettings({
                    ...emailSettings,
                    welcomeEmails: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>Course Update Emails</label>
              <p className="setting-description">
                Notify users about course updates
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={emailSettings.courseUpdates}
                onChange={(e) =>
                  setEmailSettings({
                    ...emailSettings,
                    courseUpdates: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>

      <button className="test-connection-btn">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
        Test SMTP Connection
      </button>
    </div>
  );

  const renderPaymentSettings = () => (
    <div className="settings-section">
      <h3>Payment Configuration</h3>
      <p className="section-description">
        Configure payment gateways and commission settings
      </p>

      <div className="settings-grid">
        <div className="setting-item">
          <label>Default Currency</label>
          <select
            value={paymentSettings.currency}
            onChange={(e) =>
              setPaymentSettings({
                ...paymentSettings,
                currency: e.target.value,
              })
            }
          >
            <option value="USD">USD ($)</option>
            <option value="PKR">PKR (₨)</option>
            <option value="EUR">EUR (€)</option>
            <option value="GBP">GBP (£)</option>
          </select>
        </div>

        <div className="setting-item">
          <label>Platform Commission (%)</label>
          <input
            type="number"
            value={paymentSettings.commissionRate}
            onChange={(e) =>
              setPaymentSettings({
                ...paymentSettings,
                commissionRate: e.target.value,
              })
            }
          />
          <p className="setting-description">
            Commission charged per transaction
          </p>
        </div>

        <div className="setting-item">
          <label>Minimum Payout Amount</label>
          <input
            type="number"
            value={paymentSettings.minimumPayout}
            onChange={(e) =>
              setPaymentSettings({
                ...paymentSettings,
                minimumPayout: e.target.value,
              })
            }
          />
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>Stripe Payment Gateway</label>
              <p className="setting-description">Enable Stripe for payments</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={paymentSettings.stripeEnabled}
                onChange={(e) =>
                  setPaymentSettings({
                    ...paymentSettings,
                    stripeEnabled: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>PayPal Payment Gateway</label>
              <p className="setting-description">Enable PayPal for payments</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={paymentSettings.paypalEnabled}
                onChange={(e) =>
                  setPaymentSettings({
                    ...paymentSettings,
                    paypalEnabled: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>Automatic Payouts</label>
              <p className="setting-description">
                Automatically process mentor payouts
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={paymentSettings.autoPayouts}
                onChange={(e) =>
                  setPaymentSettings({
                    ...paymentSettings,
                    autoPayouts: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section">
      <h3>Notification Preferences</h3>
      <p className="section-description">
        Configure notification delivery settings
      </p>

      <div className="settings-grid">
        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>Push Notifications</label>
              <p className="setting-description">
                Enable browser push notifications
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationSettings.pushNotifications}
                onChange={(e) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    pushNotifications: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        <div className="setting-item">
          <label>Email Digest Frequency</label>
          <select
            value={notificationSettings.emailDigest}
            onChange={(e) =>
              setNotificationSettings({
                ...notificationSettings,
                emailDigest: e.target.value,
              })
            }
          >
            <option value="realtime">Real-time</option>
            <option value="daily">Daily Digest</option>
            <option value="weekly">Weekly Digest</option>
            <option value="never">Never</option>
          </select>
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>Student Registration Alerts</label>
              <p className="setting-description">
                Notify when new students register
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationSettings.studentRegistration}
                onChange={(e) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    studentRegistration: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>System Alerts</label>
              <p className="setting-description">
                Notify about system errors and warnings
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationSettings.systemAlerts}
                onChange={(e) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    systemAlerts: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAPISettings = () => (
    <div className="settings-section">
      <h3>API Configuration</h3>
      <p className="section-description">
        Manage API keys and webhook settings
      </p>

      <div className="settings-grid">
        <div className="setting-item">
          <label>API Key</label>
          <div className="api-key-field">
            <input type="password" value={apiSettings.apiKey} readOnly />
            <button className="regenerate-btn">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
              Regenerate
            </button>
          </div>
        </div>

        <div className="setting-item">
          <label>API Version</label>
          <select
            value={apiSettings.apiVersion}
            onChange={(e) =>
              setApiSettings({ ...apiSettings, apiVersion: e.target.value })
            }
          >
            <option value="v1.0">v1.0 (Legacy)</option>
            <option value="v2.0">v2.0 (Current)</option>
            <option value="v3.0">v3.0 (Beta)</option>
          </select>
        </div>

        <div className="setting-item">
          <label>Rate Limit (per hour)</label>
          <input
            type="number"
            value={apiSettings.rateLimitPerHour}
            onChange={(e) =>
              setApiSettings({
                ...apiSettings,
                rateLimitPerHour: e.target.value,
              })
            }
          />
        </div>

        <div className="setting-item toggle">
          <div className="toggle-content">
            <div>
              <label>Webhooks Enabled</label>
              <p className="setting-description">
                Enable webhook event notifications
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={apiSettings.webhooksEnabled}
                onChange={(e) =>
                  setApiSettings({
                    ...apiSettings,
                    webhooksEnabled: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        <div className="setting-item full-width">
          <label>Webhook URL</label>
          <input
            type="url"
            value={apiSettings.webhookUrl}
            onChange={(e) =>
              setApiSettings({ ...apiSettings, webhookUrl: e.target.value })
            }
          />
        </div>
      </div>

      <div className="api-documentation">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
        </svg>
        <div>
          <h4>API Documentation</h4>
          <p>View comprehensive API documentation and integration guides</p>
        </div>
        <button className="view-docs-btn">View Docs</button>
      </div>
    </div>
  );

  const sections = [
    { id: "general", label: "General", icon: "settings" },
    { id: "security", label: "Security", icon: "shield" },
    { id: "email", label: "Email", icon: "mail" },
    // { id: "payment", label: "Payment", icon: "dollar" },
    { id: "notifications", label: "Notifications", icon: "bell" },
    // { id: "api", label: "API", icon: "code" },
  ];

  const getIcon = (type) => {
    const icons = {
      settings: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M12 1v6m0 6v6m5.2-13.2l-4.2 4.2m-2 2l-4.2 4.2M23 12h-6m-6 0H1m18.2 5.2l-4.2-4.2m-2-2l-4.2-4.2"></path>
        </svg>
      ),
      shield: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      ),
      mail: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
          <polyline points="22,6 12,13 2,6"></polyline>
        </svg>
      ),
      dollar: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="12" y1="1" x2="12" y2="23"></line>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      ),
      bell: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
      ),
      code: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="16 18 22 12 16 6"></polyline>
          <polyline points="8 6 2 12 8 18"></polyline>
        </svg>
      ),
    };
    return icons[type];
  };

  return (
    <AdminLayout>
      <div className="system-settings-container">
        {/* Header */}
        <div className="settings-header">
          <div>
            <h2>Configure System Settings</h2>
            <p className="settings-subtitle">
              Configure and manage platform settings
            </p>
          </div>
          {/* <button className="def-martins-btn">Def Martins</button> */}
        </div>

        {/* Save Notification */}
        {showSaveNotification && (
          <div className="save-notification">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Settings saved successfully!
          </div>
        )}

        <div className="settings-layout">
          {/* Sidebar Navigation */}
          <div className="settings-sidebar">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`sidebar-item ${
                  activeSection === section.id ? "active" : ""
                }`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="sidebar-icon">{getIcon(section.icon)}</span>
                <span className="sidebar-label">{section.label}</span>
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="settings-content">
            {activeSection === "general" && renderGeneralSettings()}
            {activeSection === "security" && renderSecuritySettings()}
            {activeSection === "email" && renderEmailSettings()}
            {activeSection === "payment" && renderPaymentSettings()}
            {activeSection === "notifications" && renderNotificationSettings()}
            {activeSection === "api" && renderAPISettings()}

            {/* Action Buttons */}
            <div className="settings-actions">
              <button className="cancel-btn">Cancel</button>
              <button className="save-btn" onClick={handleSave}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SystemSettings;
